import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import { useCompareUrls } from '../shared/heicDisplay';
import {
    IconDownload,
    IconLock,
    IconLockOpen,
    IconPercentage,
    IconRefresh,
    IconRuler2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import Hint from '../../../../../components/common/Hint';
import ToolCard from '../../components/common/ToolCard';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    isResizeConfigValid,
    isSupportedResizeImageFile,
    readImageDimensions,
    resizeImageFile,
} from './resizeImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

const PERCENT_OPTIONS = [25, 50, 75, 100, 150, 200];

const MODE_OPTIONS = [
    { id: 'pixels', labelKey: 'resizeImage.modePixels' },
    { id: 'percent', labelKey: 'resizeImage.modePercent' },
];

const MODE_ICONS = { pixels: IconRuler2, percent: IconPercentage };

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

function chipBtnClass(active) {
    return `rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-amber-400/40 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

const inputClass = 'w-full rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[13px] text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 disabled:opacity-45 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

const fileKey = (file) => `${file.name}-${file.size}`;

export default function ResizeImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    // Which picture the width/height boxes were last seeded from. Adding more
    // files must not overwrite dimensions the user has already typed.
    const seededKeyRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [mode, setMode] = useState('pixels');
    const [width, setWidth] = useState('1280');
    const [height, setHeight] = useState('720');
    const [percent, setPercent] = useState(50);
    const [format, setFormat] = useState('original');
    const [lockAspect, setLockAspect] = useState(true);
    const [dimensionDriver, setDimensionDriver] = useState('width');
    const [refAspect, setRefAspect] = useState(16 / 9);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    // The first picture seeds the boxes and the locked aspect ratio.
    useEffect(() => {
        const first = files[0];
        if (!first) {
            seededKeyRef.current = null;
            return undefined;
        }
        const key = fileKey(first);
        if (seededKeyRef.current === key) return undefined;
        seededKeyRef.current = key;

        let cancelled = false;
        readImageDimensions(first).then((dims) => {
            if (cancelled) return;
            setRefAspect(dims.width / dims.height);
            setWidth(String(dims.width));
            setHeight(String(dims.height));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [files]);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedResizeImageFile);
        if (!images.length) {
            const msg = t('resizeImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('resizeImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        invalidateResults();
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // The edits rewrite the picked file, so the pipeline below is untouched.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            invalidateResults();
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            invalidateResults();
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        setMode('pixels');
        setWidth('1280');
        setHeight('720');
        setPercent(50);
        setLockAspect(true);
        invalidateResults();
    };

    const handleWidthChange = (value) => {
        setWidth(value);
        setDimensionDriver('width');
        if (lockAspect && refAspect > 0) {
            const w = Number(value);
            if (w > 0) setHeight(String(Math.max(1, Math.round(w / refAspect))));
        }
        invalidateResults();
    };

    const handleHeightChange = (value) => {
        setHeight(value);
        setDimensionDriver('height');
        if (lockAspect && refAspect > 0) {
            const h = Number(value);
            if (h > 0) setWidth(String(Math.max(1, Math.round(h * refAspect))));
        }
        invalidateResults();
    };

    const resizeOptions = useMemo(() => ({
        mode,
        width: Number(width),
        height: Number(height),
        percent,
        lockAspect,
        dimensionDriver,
    }), [mode, width, height, percent, lockAspect, dimensionDriver]);

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already resized is redone.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canResize = pendingIndexes.length > 0 && isResizeConfigValid(mode, resizeOptions);

    const handleResize = async () => {
        if (!canResize || processing) return;

        recordToolUsage('image/resize', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length });
                const result = await resizeImageFile(files[i], { ...resizeOptions, format });

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }
            notifications.show({ title: t('common.success'), message: t('resizeImage.done'), color: 'green' });
        } catch {
            const msg = t('resizeImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;
        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }
        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'resized-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: canResize,
        processing,
        hasResult: doneResults.length > 0,
        hasError: !!error,
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // HEIC originals are decoded for the "before" pane (see shared/heicDisplay).
    const compareUrls = useCompareUrls(results, files);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/jpeg',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const successLabel = doneResults.length > 1
        ? `${t('resizeImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('resizeImage.done');

    const processingLabel = progress?.fileTotal > 1
        ? t('resizeImage.progressFileOnly', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('resizeImage.resizing');

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('resizeImage.done'),
    });

    const settingsLocked = processing;

    return (
        <>
        <ToolPageLayout
            slug="image/resize"
            subtitleKey="resizeImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('resizeImage.downloadZip') : t('resizeImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('resizeImage.resize'),
                onClick: handleResize,
                disabled: !canResize || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('resizeImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    <ToolCard title={t('resizeImage.modeTitle')}>
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only — the name is in the tooltip and read
                                out via aria-label. */}
                            {MODE_OPTIONS.map((opt) => {
                                const Icon = MODE_ICONS[opt.id];
                                const label = t(opt.labelKey);
                                return (
                                    <Hint label={label} key={opt.id}>
                                    <button
                                        type="button"
                                        onClick={() => { setMode(opt.id); invalidateResults(); }}
                                        disabled={settingsLocked}
                                        className={segBtnClass(mode === opt.id)}
                                        aria-label={label}
                                        aria-pressed={mode === opt.id}
                                    >
                                        <Icon size={20} stroke={1.8} />
                                    </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </ToolCard>

                    {mode === 'pixels' ? (
                        <ToolCard title={t('resizeImage.dimensionsTitle')}>
                            {/* Width, height and the ratio lock read as one control,
                                so they share a row — the lock sits where it acts,
                                between the two boxes it ties together. */}
                            <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                                <label className="block space-y-1.5">
                                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('resizeImage.widthLabel')}
                                    </span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={10000}
                                        value={width}
                                        disabled={settingsLocked}
                                        onChange={(e) => handleWidthChange(e.target.value)}
                                        className={inputClass}
                                    />
                                </label>
                                <label className="block space-y-1.5">
                                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('resizeImage.heightLabel')}
                                    </span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={10000}
                                        value={height}
                                        disabled={settingsLocked}
                                        onChange={(e) => handleHeightChange(e.target.value)}
                                        className={inputClass}
                                    />
                                </label>
                                <Hint label={t('resizeImage.lockAspect')}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLockAspect((prev) => !prev);
                                        invalidateResults();
                                    }}
                                    disabled={settingsLocked}
                                    aria-label={t('resizeImage.lockAspect')}
                                    aria-pressed={lockAspect}
                                    className={`inline-flex h-[38px] w-[38px] items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                                        lockAspect
                                            ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
                                            : 'border-[#ECE8E4] text-[#6B635C] dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {lockAspect ? <IconLock size={17} /> : <IconLockOpen size={17} />}
                                </button>
                                </Hint>
                            </div>
                        </ToolCard>
                    ) : (
                        <ToolCard title={t('resizeImage.percentTitle')}>
                            {/* Six presets, three to a row — two even lines instead
                                of a ragged wrap with one chip stranded below. */}
                            <div className="grid grid-cols-3 gap-2">
                                {PERCENT_OPTIONS.map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => { setPercent(value); invalidateResults(); }}
                                        disabled={settingsLocked}
                                        className={chipBtnClass(percent === value)}
                                    >
                                        {toBnDigits(value)}%
                                    </button>
                                ))}
                            </div>
                            <label className="mt-3 block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('resizeImage.customPercent')}
                                </span>
                                <input
                                    type="number"
                                    min={1}
                                    max={500}
                                    value={percent}
                                    disabled={settingsLocked}
                                    onChange={(e) => { setPercent(Number(e.target.value)); invalidateResults(); }}
                                    className={inputClass}
                                />
                            </label>
                        </ToolCard>
                    )}

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        disabled={settingsLocked}
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('resizeImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('resizeImage.previewEmpty')}
                    idleHint={t('resizeImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.fileTotal
                        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
                        : undefined}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    showPasteHint
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished files can be opened. */}
                    {files.length > 0 && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            actions={processing || doneResults.length ? [] : fileActions}
                            files={files}
                            results={results}
                            variant="resize"
                            busyIndexes={processing ? pendingIndexes : undefined}
                            activeIndex={doneResults.length ? previewIndex : undefined}
                            onItemClick={doneResults.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('resizeImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            // `adjusted` is the resized image re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
